import { supabaseClient } from '@plataforma/supabase-client';

import { obtenerEstadoSesion } from '../estado-sesion.js';

import { marcarCambiosPendientes } from './estado-publicacion.js';

export const CLAVE_TEMA_VISUAL = 'tema_visual';

const REGEX_COLOR_HEX = /^#[0-9a-fA-F]{6}$/;

export const CABECERAS_PERMITIDAS = new Set(['clasico', 'ecommerce', 'minimal', 'premium']);
export const ESTILOS_BOTON_PERMITIDOS = new Set(['solido', 'contorno', 'redondeado', 'limpio']);
export const ESTILOS_TARJETA_PERMITIDOS = new Set(['clara', 'sombra', 'contorno', 'minima']);

export const TEMA_VISUAL_POR_DEFECTO = {
  color_primario: '#116b55',
  color_secundario: '#bf3f32',
  color_botones: '#116b55',
  color_botones_hover: '#0b493b',
  color_texto: '#17211d',
  color_header: '#ffffff',
  color_footer: '#10263a',
  color_enlaces: '#116b55',
  color_fondo: '#f7f5ef',
  color_tarjetas: '#ffffff',
  radio_bordes: '8',
  tipografia: 'inter',
  sombras: 'suave',
  espaciado: 'normal',
  // Campos del constructor visual (Parte 1). Mantienen la compatibilidad con
  // temas guardados antes de existir: normalizarTemaVisual los completa con
  // estos valores por defecto y la publicación antigua sigue funcionando.
  tema_id: null,
  cabecera: 'clasico',
  cabecera_fija: true,
  cabecera_alineacion: 'izquierda',
  cabecera_whatsapp: true,
  boton_estilo: 'solido',
  tarjeta_estilo: 'clara',
};

export const CAMPOS_COLOR_TEMA = [
  'color_primario',
  'color_secundario',
  'color_botones',
  'color_botones_hover',
  'color_texto',
  'color_header',
  'color_footer',
  'color_enlaces',
  'color_fondo',
  'color_tarjetas',
];

const TIPOGRAFIAS_PERMITIDAS = new Set(['inter', 'system', 'serif', 'mono', 'rounded']);
const SOMBRAS_PERMITIDAS = new Set(['ninguna', 'suave', 'media', 'fuerte']);
const ESPACIADOS_PERMITIDOS = new Set(['compacto', 'normal', 'amplio']);

function empresaIdActual() {
  return obtenerEstadoSesion().claims.empresa_id;
}

export function esColorHexValido(valor) {
  return typeof valor === 'string' && REGEX_COLOR_HEX.test(valor.trim());
}

export function esRadioValido(valor) {
  const numero = Number(valor);
  return Number.isFinite(numero) && numero >= 0 && numero <= 32;
}

function coaccionarBooleano(valor, defecto) {
  if (typeof valor === 'string') return valor === 'true' || valor === '1';
  if (typeof valor === 'boolean') return valor;
  if (typeof valor === 'number') return valor === 1;
  return defecto;
}

export function normalizarTemaVisual(tema) {
  const base = { ...TEMA_VISUAL_POR_DEFECTO, ...(tema ?? {}) };
  return {
    ...base,
    cabecera_fija: coaccionarBooleano(base.cabecera_fija, true),
    cabecera_whatsapp: coaccionarBooleano(base.cabecera_whatsapp, true),
    radio_bordes: String(base.radio_bordes ?? TEMA_VISUAL_POR_DEFECTO.radio_bordes),
  };
}

function validarTemaVisual(tema) {
  for (const campo of CAMPOS_COLOR_TEMA) {
    if (!esColorHexValido(tema[campo])) return `El campo ${campo} debe tener formato #rrggbb.`;
  }

  if (!esRadioValido(tema.radio_bordes)) return 'El radio de bordes debe estar entre 0 y 32 px.';
  if (!TIPOGRAFIAS_PERMITIDAS.has(tema.tipografia)) return 'La tipografia elegida no es valida.';
  if (!SOMBRAS_PERMITIDAS.has(tema.sombras)) return 'La sombra elegida no es valida.';
  if (!ESPACIADOS_PERMITIDOS.has(tema.espaciado)) return 'El espaciado elegido no es valido.';

  if (!CABECERAS_PERMITIDAS.has(tema.cabecera)) return 'La variante de encabezado no es valida.';
  if (!['izquierda', 'centrada'].includes(tema.cabecera_alineacion)) {
    return 'La alineacion del encabezado no es valida.';
  }
  if (!ESTILOS_BOTON_PERMITIDOS.has(tema.boton_estilo)) return 'El estilo de botones no es valido.';
  if (!ESTILOS_TARJETA_PERMITIDOS.has(tema.tarjeta_estilo))
    return 'El estilo de tarjetas no es valido.';

  return null;
}

export async function obtenerTemaVisual() {
  const { data, error } = await supabaseClient()
    .from('configuracion_empresa')
    .select('valor')
    .eq('empresa_id', empresaIdActual())
    .eq('clave', CLAVE_TEMA_VISUAL)
    .maybeSingle();

  if (error) return { data: TEMA_VISUAL_POR_DEFECTO, error };

  return { data: normalizarTemaVisual(data?.valor), error: null };
}

/**
 * Campos del constructor visual que `normalizarTemaVisual` completa con
 * valores por defecto. Se usan para decidir cuándo una guardada es "parcial"
 * (p. ej. colores sueltos) y por lo tanto debe fusionarse sobre lo ya
 * guardado en lugar de resetear piezas del editor.
 */
const CAMPOS_CONSTRUCTOR = [
  'tema_id',
  'cabecera',
  'cabecera_fija',
  'cabecera_alineacion',
  'cabecera_whatsapp',
  'boton_estilo',
  'tarjeta_estilo',
];

function esTemaCompleto(tema) {
  return CAMPOS_CONSTRUCTOR.every((campo) => (tema ?? {})[campo] !== undefined);
}

export async function guardarTemaVisual(tema) {
  let temaNormalizado = normalizarTemaVisual(tema);

  // Una guardada PARCIAL (típica de configuraciones antiguas o flujos que
  // solo tocan colores/tipografía) jamás debe resetear a los valores por
  // defecto los campos del constructor (cabecera, boton_estilo, tarjeta_estilo,
  // cabecera_fija, ...) que ya estaban configurados por la Parte 1.
  if (!esTemaCompleto(tema)) {
    const { data: existente } = await supabaseClient()
      .from('configuracion_empresa')
      .select('valor')
      .eq('empresa_id', empresaIdActual())
      .eq('clave', CLAVE_TEMA_VISUAL)
      .maybeSingle();
    temaNormalizado = normalizarTemaVisual({ ...(existente?.valor ?? {}), ...(tema ?? {}) });
  }

  const errorValidacion = validarTemaVisual(temaNormalizado);

  if (errorValidacion) {
    return { data: null, error: new Error(errorValidacion) };
  }

  const resultado = await supabaseClient()
    .from('configuracion_empresa')
    .upsert(
      {
        empresa_id: empresaIdActual(),
        clave: CLAVE_TEMA_VISUAL,
        valor: {
          ...temaNormalizado,
          radio_bordes: String(Number(temaNormalizado.radio_bordes)),
        },
      },
      { onConflict: 'empresa_id,clave' },
    )
    .select()
    .single();

  // Un cambio en tema_visual no se refleja en la tienda pública hasta
  // publicar (y configuracion_empresa no tiene `actualizado_en` con el que
  // detectarlo desde la BD): encendemos el marcador local de "pendiente".
  if (!resultado.error) marcarCambiosPendientes();

  return resultado;
}
